import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Dedicated itinerary-reprice approval queue (Task 6).
 *
 * A DISCOUNT reprice that erodes more than 2% of the gross markup writes a
 * PENDING approval row routed through the lead reviewer hierarchy (requester ->
 * reporting SALES_LEAD -> admin pool; ADMIN auto-approves). This is its own
 * table / queue, separate from the lead status-change approvals, but reuses the
 * same routing + reviewer-scoping helpers.
 */

const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });

export const RepriceApprovalStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'SUPERSEDED',
]);
export type RepriceApprovalStatus = z.infer<typeof RepriceApprovalStatusSchema>;

/**
 * What a reprice approval row represents.
 *   - DISCOUNT        : the original itinerary discount reprice (target grand
 *                       total; uses targetPaise/amountPaise/marginPct/lever).
 *   - INSTALMENT_SPLIT: a seller lowering the first instalment after the itinerary
 *                       was sent; the shortfall shifts to instalment 2 and its due
 *                       date moves one day earlier. Data lives in `instalmentSplit`.
 */
export const RepriceApprovalKindSchema = z.enum([
  'DISCOUNT',
  'INSTALMENT_SPLIT',
  'INSTALMENT_DATE_CHANGE',
]);
export type RepriceApprovalKind = z.infer<typeof RepriceApprovalKindSchema>;

/**
 * The before/after payload of an INSTALMENT_SPLIT reprice (paise + ISO dates).
 *
 * Two modes:
 *   - FOLD: the shortfall is added to instalment 2 and its due date moves one
 *     day earlier (the original behaviour). `i2*` fields carry the change.
 *   - NEW : the shortfall becomes a brand-new instalment inserted right after
 *     instalment 1 (later instalments shift down a seq), due on
 *     `newInstalmentDueDate`. Instalment 2 is untouched, so the `i2*` fields
 *     are null.
 */
export const InstalmentSplitSchema = z.object({
  mode: z.enum(['FOLD', 'NEW']).default('FOLD'),
  /** The first instalment row being lowered. */
  i1InstalmentId: UuidSchema,
  i1Paise: z.string(),
  originalI1Paise: z.string(),
  /** FOLD mode: the second instalment row the shortfall shifts into. Null for NEW. */
  i2InstalmentId: UuidSchema.nullable().default(null),
  i2Paise: z.string().nullable().default(null),
  /** New instalment-2 due date (YYYY-MM-DD) - original minus one day. Null for NEW. */
  i2DueDate: z.string().nullable().default(null),
  originalI2Paise: z.string().nullable().default(null),
  originalI2DueDate: z.string().nullable().default(null),
  /** NEW mode: the shortfall instalment's amount + due date. Null for FOLD. */
  newInstalmentPaise: z.string().nullable().default(null),
  newInstalmentDueDate: z.string().nullable().default(null),
});
export type InstalmentSplit = z.infer<typeof InstalmentSplitSchema>;

/** The payload of an INSTALMENT_DATE_CHANGE reprice: the new due date AND amount
 *  of each unpaid instalment (works on PREVIEW and LOCKED/won plans). Amounts are
 *  optional - a pure date move leaves them null. The plan total is preserved: any
 *  leftover from the amount edits either becomes a brand-new instalment
 *  (`newInstalment`) or is folded into an existing one (`foldInstalmentId`). */
export const InstalmentDateChangeSchema = z.object({
  changes: z
    .array(
      z.object({
        instalmentId: UuidSchema,
        /** YYYY-MM-DD. */
        oldDueDate: z.string(),
        newDueDate: z.string(),
        /** Paise before/after, when the amount changed; null for a date-only move. */
        oldAmountPaise: z.string().nullable().default(null),
        newAmountPaise: z.string().nullable().default(null),
      }),
    )
    .min(1),
  /** A new instalment created to absorb a leftover (amounts reduced overall). A
   *  fold (the other option) is baked into the target change's new amount above. */
  newInstalment: z
    .object({ amountPaise: z.string(), dueDate: z.string() })
    .nullable()
    .default(null),
});
export type InstalmentDateChange = z.infer<typeof InstalmentDateChangeSchema>;

/**
 * A single reprice-approval row as returned by the queue list. Money amounts
 * are bigint paise serialized as decimal strings; `marginPct` is a plain
 * number (the discount as a percent of gross markup at request time).
 */
export const RepriceApprovalListItemSchema = z.object({
  id: UuidSchema,
  itineraryId: UuidSchema,
  leadId: UuidSchema.nullable(),
  leadReference: z.string().nullable(),
  customerName: z.string().nullable(),
  itineraryTitle: z.string().nullable(),
  status: RepriceApprovalStatusSchema,
  /// DISCOUNT (default) or INSTALMENT_SPLIT. The discount money fields below are
  /// null for INSTALMENT_SPLIT rows, which carry `instalmentSplit` instead.
  kind: RepriceApprovalKindSchema,
  targetPaise: z.string().nullable(),
  /// Pre-reprice ("from") grand total. Null for rows created before tracking.
  originalGrandPaise: z.string().nullable(),
  lever: z.string().nullable(),
  amountPaise: z.string().nullable(),
  achievedGrandPaise: z.string().nullable(),
  marginPct: z.number().nullable(),
  /// INSTALMENT_SPLIT payload (null for DISCOUNT rows).
  instalmentSplit: InstalmentSplitSchema.nullable(),
  /// INSTALMENT_DATE_CHANGE payload (null for other kinds).
  instalmentDateChange: InstalmentDateChangeSchema.nullable().default(null),
  /// Markup % on the pre-tax net cost BEFORE this reprice (netMarkup / netCost).
  /// ADMIN-only in the UI. Null when the itinerary has no cost recorded.
  currentMarkupPct: z.number().nullable(),
  /// Markup % AFTER this reprice = (netMarkup - discount + upsell) / netCost.
  /// ADMIN-only in the UI. Null when the itinerary has no cost recorded.
  newMarkupPct: z.number().nullable(),
  requestRemark: z.string().nullable(),
  reviewRemark: z.string().nullable(),
  requestedBy: UserRefSchema,
  /// When set, this SALES_LEAD reviews the request. Null = admin pool.
  assignedReviewer: UserRefSchema.nullable(),
  reviewedBy: UserRefSchema.nullable(),
  reviewedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type RepriceApprovalListItem = z.infer<typeof RepriceApprovalListItemSchema>;

/** Detail shape is currently identical to the list row. */
export const RepriceApprovalDetailSchema = RepriceApprovalListItemSchema;
export type RepriceApprovalDetail = z.infer<typeof RepriceApprovalDetailSchema>;

export const RepriceApprovalListQuerySchema = PaginationQuerySchema.extend({
  // Omit to return every status as a complete record; a value narrows it.
  status: RepriceApprovalStatusSchema.optional(),
  /** Omit for every type; a value narrows to DISCOUNT or INSTALMENT_SPLIT. */
  kind: RepriceApprovalKindSchema.optional(),
  /**
   * Narrow to one team member's submissions. Always ANDed with the caller's
   * reviewer scope server-side, so it can narrow a SALES_LEAD's view, never
   * widen it past the rows assigned to them.
   */
  requestedById: UuidSchema.optional(),
});
export type RepriceApprovalListQuery = z.infer<typeof RepriceApprovalListQuerySchema>;

export const RepriceApprovalListResponseSchema = paginatedResponseSchema(
  RepriceApprovalListItemSchema,
);
export type RepriceApprovalListResponse = z.infer<typeof RepriceApprovalListResponseSchema>;

/**
 * Pending queue depth for the caller, reviewer-scoped exactly like `list()`
 * (ADMIN all, SALES_LEAD assigned, SALES_AGENT their own). Backs the sidebar
 * nav red count badge, mirroring the lead-approvals pending-count endpoint.
 */
export const RepriceApprovalPendingCountSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type RepriceApprovalPendingCount = z.infer<typeof RepriceApprovalPendingCountSchema>;

/** Body for `POST /itinerary-reprice-approvals/:id/approve`. Remark optional. */
export const RepriceApprovalApproveRequestSchema = z.object({
  remark: z.string().trim().max(2000).optional(),
});
export type RepriceApprovalApproveRequest = z.infer<typeof RepriceApprovalApproveRequestSchema>;

/** Body for `POST /itinerary-reprice-approvals/:id/reject`. Remark REQUIRED. */
export const RepriceApprovalRejectRequestSchema = z.object({
  remark: z.string().trim().min(1, 'A remark is required when rejecting a reprice').max(2000),
});
export type RepriceApprovalRejectRequest = z.infer<typeof RepriceApprovalRejectRequestSchema>;

/**
 * Body for `POST /payments/plan/:itineraryId/instalment-split`: the seller lowers
 * the first instalment to `firstInstalmentPaise` (> 0 and <= the auto-calculated
 * first instalment). The shortfall shifts to instalment 2 and its due date moves
 * one day earlier. Routes through the reprice-approval queue (kind
 * INSTALMENT_SPLIT); the plan only changes once approved. An optional remark is
 * shown to the reviewer.
 */
export const InstalmentSplitProposeRequestSchema = z
  .object({
    firstInstalmentPaise: z.number().int().positive(),
    /**
     * true (default): the shortfall becomes a NEW instalment inserted after
     * instalment 1, due on `newInstalmentDueDate`. false: the shortfall folds
     * into instalment 2 (its due date moves one day earlier) - the original
     * behaviour.
     */
    createNewInstalment: z.boolean().default(true),
    /** Required when `createNewInstalment` is true. YYYY-MM-DD. */
    newInstalmentDueDate: z.string().optional(),
    remark: z.string().trim().max(2000).optional(),
  })
  .refine((v) => !v.createNewInstalment || !!v.newInstalmentDueDate, {
    message: 'A due date is required for the new instalment.',
    path: ['newInstalmentDueDate'],
  });
export type InstalmentSplitProposeRequest = z.infer<typeof InstalmentSplitProposeRequestSchema>;

/**
 * Body for `POST /payments/plan/:itineraryId/instalment-dates`: the desired due
 * date AND amount of EVERY unpaid instalment (the editor sends them all). Works on
 * PREVIEW and LOCKED (won) plans. The plan total is preserved: if the amounts no
 * longer add up to the current unpaid total, the leftover (when the amounts were
 * reduced overall) becomes a NEW instalment on `newInstalmentDueDate` if
 * `createNewInstalment` is set, else it folds into the last unpaid instalment;
 * when the amounts exceed the total the overflow is always taken off the last
 * unpaid instalment. Routes through the reprice-approval queue (kind
 * INSTALMENT_DATE_CHANGE); an ADMIN auto-approves, everyone else goes to the inbox.
 */
export const InstalmentDateChangeProposeRequestSchema = z
  .object({
    changes: z
      .array(
        z.object({
          instalmentId: UuidSchema,
          /** YYYY-MM-DD. */
          dueDate: z.string(),
          /** Desired amount in paise. Omit to keep the current amount. */
          amountPaise: z.number().int().positive().optional(),
        }),
      )
      .min(1),
    /** true: a positive leftover becomes a new instalment on `newInstalmentDueDate`. */
    createNewInstalment: z.boolean().default(false),
    /** Required when `createNewInstalment` is true. YYYY-MM-DD. */
    newInstalmentDueDate: z.string().optional(),
    remark: z.string().trim().max(2000).optional(),
  })
  .refine((v) => !v.createNewInstalment || !!v.newInstalmentDueDate, {
    message: 'A due date is required for the new instalment.',
    path: ['newInstalmentDueDate'],
  });
export type InstalmentDateChangeProposeRequest = z.infer<
  typeof InstalmentDateChangeProposeRequestSchema
>;
